import React, { CSSProperties } from 'react';
import classNames from 'classnames';
import { InView } from 'react-intersection-observer';
import Box from '../Box';
import Button from '../Button';
import CloudinaryImage from '../CloudinaryImage';
import Stack from '../Stack';
import Text from '../Text';
import useScreenSize from '../_internal/useScreenSize';

import styles from './crossSellPromo.module.scss';

type Variant = 'hero' | 'fix' | 'condensed';

const variantImageLayouts: Record<
  Variant,
  { aspectRatio: number; style: CSSProperties }[]
> = {
  hero: [
    {
      aspectRatio: 3 / 3,
      style: { gridColumn: '1 / span 3', gridRow: '1 / span 3' },
    },
    {
      aspectRatio: 2 / 3,
      style: { gridColumn: '5 / span 2', gridRow: '1 / span 3' },
    },
  ],
  fix: [
    {
      aspectRatio: 3 / 2,
      style: { gridColumn: '1 / span 3', gridRow: '1 / span 2' },
    },
  ],
  condensed: [
    {
      aspectRatio: 3 / 2,
      style: { gridColumn: '1 / span 3', gridRow: '1 / span 2' },
    },
  ],
};

interface Image {
  /**
   * The alt text for the image.
   */
  altText: string;
  /**
   * The base URL for an image hosted on Cloudinary (or Cloudfront CDN).
   *
   * Cloudinary example: `https://res.cloudinary.com/stitch-fix/image/upload/`
   *
   * Cloudfront example: `https://d3n78nkjl8tizo.cloudfront.net/stitch-fix/image/upload/`
   */
  baseUrl: string;
  /**
   * A Cloudinary image's name (version and/or public id)
   *
   * Example: `/v1585276216/a6ttgw2xjomdl1pbzehz.jpg`
   */
  mediaPath: string;
}

export interface CrossSellPromoProps {
  /**
   * Background image props with responsive values for controlling condensed and expanded layouts.
   */
  image: Image;
  cta: {
    /**
     * Cta text copy
     */
    text: string;
    /**
     * Cta destination path
     */
    destinationPath?: string;
  };
  /**
   * Expanded content for larger screen size layouts
   */
  expandedContent: {
    cta: {
      /**
       * Cta text copy
       */
      text: string;
      /**
       * Cta destination path
       */
      destinationPath?: string;
    };
    /**
     * Description copy
     */
    description?: string;
    /**
     * Images for grid layouts
     */
    images: Image[];
    /**
     * Title copy
     */
    title?: string;
  };
  /**
   * Variant style
   */
  variant: Variant;
  /**
   * A callback function that is called when the component viewport status changes.
   */
  onInView?: (inView: boolean) => void;
  /**
   * A callback function that is called when user clicks cta button or image
   */
  onClick?: ((e: React.MouseEvent<HTMLAnchorElement>) => void) | (() => void);
}

/**
 * The `CrossSellPromo` is a responsive component that populates a promotion image, text, and cta.
 * The component showcases 1 promotional image on small viewports and multiple images on larger
 * ports.
 */
const CrossSellPromo = ({
  image,
  cta,
  expandedContent,
  variant,
  onClick,
  onInView,
}: CrossSellPromoProps) => {
  const screenSize = useScreenSize();

  // Copy displayed in left column on larger screens
  const expandedCopy = (
    <Stack spacing={1.5} mt="auto" mb="auto">
      <Stack spacing={0.5}>
        <Text color="blue-45" setting="title-medium">
          {expandedContent.title}
        </Text>
        <Text color="blue-45" setting="body-medium">
          {expandedContent.description}
        </Text>
      </Stack>

      <Button
        onClick={onClick}
        variant="filled-staple"
        {...(expandedContent.cta.destinationPath
          ? { as: 'a', href: expandedContent.cta.destinationPath }
          : {})}
      >
        {expandedContent.cta.text}
      </Button>
    </Stack>
  );

  // Use expanded images on larger screens
  const images =
    screenSize === 'sm' || variant === 'condensed'
      ? [image]
      : expandedContent.images;

  const responsiveImages = images.map(
    ({ altText, baseUrl, mediaPath }, imageIndex) => (
      <Box
        key={mediaPath}
        style={variantImageLayouts[variant][imageIndex].style}
      >
        <CloudinaryImage
          key={mediaPath}
          alt={`${altText}`}
          baseUrl={`${baseUrl}`}
          mediaPath={`${mediaPath}`}
          sizes={{
            sm: 600 * variantImageLayouts[variant][imageIndex].aspectRatio,
          }}
        />
      </Box>
    ),
  );

  return (
    <InView
      threshold={0.5}
      onChange={(inView: boolean) => inView && onInView?.(inView)}
      data-testid="cross-sell-observer-wrapper"
      triggerOnce
    >
      <Box
        className={classNames(styles.grid, styles[variant])}
        data-testid="cross-sell-promo"
      >
        <Box
          {...(cta.destinationPath
            ? { as: 'a', href: cta.destinationPath }
            : {})}
          onClick={onClick}
          className={styles.images}
        >
          <Box className={styles['aspect-placeholder']} />
          {responsiveImages}
        </Box>
        <Box className={styles.copy} p={{ sm: 1.5, lg: 2.25 }}>
          {expandedCopy}
        </Box>
      </Box>
    </InView>
  );
};

export default CrossSellPromo;
